import React, { useState } from 'react';
import {
    Folder,
    Search,
    X,
    Plus,
    MoreVertical,
    Pencil,
    Trash2,
    Layers,
    Check,
} from 'lucide-react';
import useStore from '@/store';
import type { Project } from '@/types';
import { apiService as api } from '@/services/api/mobileApiService';
import BottomDrawer from '@/components/ui/BottomDrawer';
import { logger } from '@/utils/logger';

/**
 * Project picker drawer. Switch the active project + manage projects
 * inline (rename / delete / open the floor-plan management screen)
 * without leaving the drawer.
 *
 * Per-row UX:
 *  - Tap the row body  → switch to that project and close the drawer
 *  - Tap the ⋮ button   → reveal a small action strip below the row
 *  - In the action strip:
 *      Rename       → row morphs into inline edit mode
 *      Manage plans → navigates to the floor-plan management screen
 *      Delete       → confirm + remove
 *
 * Drawer chrome (backdrop, handle, title, close, ESC, scroll lock)
 * is delegated to <BottomDrawer>.
 */

interface ProjectBottomSheetProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Slice of the Zustand store this drawer reads/writes. Declaring the
 * shape explicitly (rather than `as any`) keeps the action signatures
 * honest while staying a strict subset of the real store.
 */
interface ProjectDrawerStore {
    projects: Project[] | null;
    currentProject: Project | null;
    setCurrentProject: (p: Project | null) => void;
    setCurrentScreen: (name: string) => void;
    setProjects: (ps: Project[]) => void;
    addProject: (p: Project) => void;
    deleteProject: (id: number) => void;
    openFloorPlanDrawer: (projectId: number) => void;
    org: { id: number } | null;
    orgRole: string | null;
}

/** Narrow an unknown thrown value to a readable message. */
const errorMessage = (err: unknown): string | undefined =>
    err instanceof Error ? err.message : typeof err === 'string' ? err : undefined;

/** Type guard: the API returned something shaped like a persisted Project. */
const isCreatedProject = (value: unknown): value is Project =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id !== 'undefined' &&
    (value as { id?: unknown }).id !== null;

const cx = (...parts: Array<string | false | undefined>): string =>
    parts.filter(Boolean).join(' ');

const ProjectBottomSheet: React.FC<ProjectBottomSheetProps> = ({ open, onClose }) => {
    const {
        projects,
        currentProject,
        setCurrentProject,
        setCurrentScreen,
        setProjects,
        addProject,
        deleteProject: removeFromStore,
        openFloorPlanDrawer,
        org,
        orgRole,
    } = useStore() as ProjectDrawerStore;

    // Drawer view — list (pick / rename / delete) vs create (new project form).
    const [view, setView] = useState<'list' | 'create'>('list');

    const [query, setQuery] = useState('');
    const [actionsOpenFor, setActionsOpenFor] = useState<string | number | null>(null);
    const [renamingId, setRenamingId] = useState<string | number | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [busyId, setBusyId] = useState<string | number | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Create-view form state
    const [createName, setCreateName] = useState('');
    const [createDescription, setCreateDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    // Reset transient state every time the drawer closes.
    React.useEffect(() => {
        if (!open) {
            setView('list');
            setQuery('');
            setActionsOpenFor(null);
            setRenamingId(null);
            setRenameValue('');
            setBusyId(null);
            setError(null);
            setCreateName('');
            setCreateDescription('');
            setIsCreating(false);
        }
    }, [open]);

    const normalized = query.trim().toLowerCase();
    const filtered = (projects ?? []).filter(
        (p) => !normalized || p.name.toLowerCase().includes(normalized),
    );

    const select = (p: Project): void => {
        setCurrentProject(p);
        try {
            (useStore.getState() as { fetchFloorPlans?: (id: Project['id']) => void }).fetchFloorPlans?.(p.id);
        } catch {
            /* non-fatal */
        }
        setCurrentScreen('camera');
        onClose();
    };

    const deselect = (): void => {
        setCurrentProject(null);
        setCurrentScreen('camera');
        onClose();
    };

    const startCreate = (): void => {
        // Switch to inline create view — drawer stays open and morphs.
        // Cancel / success return to 'list'.
        setError(null);
        setCreateName('');
        setCreateDescription('');
        setView('create');
    };

    const cancelCreate = (): void => {
        if (isCreating) return;
        setView('list');
        setCreateName('');
        setCreateDescription('');
        setError(null);
    };

    const submitCreate = async (): Promise<void> => {
        const name = createName.trim();
        if (!name) {
            setError('Project name cannot be empty');
            return;
        }
        setIsCreating(true);
        setError(null);
        try {
            const created = await api.createProject({
                name,
                description: createDescription.trim() || 'New project',
                is_public: true,
                ...(org && orgRole === 'owner' ? { org_id: org.id } : {}),
            });
            if (!isCreatedProject(created)) {
                throw new Error('Invalid server response');
            }
            addProject(created);
            setView('list');
            setCreateName('');
            setCreateDescription('');
        } catch (err: unknown) {
            logger.error('Failed to create project', { error: errorMessage(err) });
            setError(errorMessage(err) || 'Failed to create project. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const toggleActions = (id: string | number): void => {
        setActionsOpenFor((prev) => (prev === id ? null : id));
        setRenamingId(null);
        setError(null);
    };

    const startRename = (p: Project): void => {
        setRenamingId(p.id);
        setRenameValue(p.name);
        setActionsOpenFor(null);
        setError(null);
    };

    const cancelRename = (): void => {
        setRenamingId(null);
        setRenameValue('');
        setError(null);
    };

    const saveRename = async (p: Project): Promise<void> => {
        const next = renameValue.trim();
        if (!next) {
            setError('Project name cannot be empty');
            return;
        }
        if (next === p.name) {
            cancelRename();
            return;
        }
        setBusyId(p.id);
        try {
            await api.updateProject(p.id, { name: next });
            const updated = (projects ?? []).map((proj) =>
                proj.id === p.id ? { ...proj, name: next } : proj,
            );
            setProjects(updated);
            // Keep currentProject reference fresh if we renamed it.
            if (currentProject?.id === p.id) {
                setCurrentProject({ ...currentProject, name: next });
            }
            cancelRename();
        } catch (err: unknown) {
            logger.error('Failed to rename project', { error: errorMessage(err) });
            setError('Failed to rename project.');
        } finally {
            setBusyId(null);
        }
    };

    const confirmDelete = async (p: Project): Promise<void> => {
        if (!window.confirm(`Delete project "${p.name}"?`)) return;
        setBusyId(p.id);
        try {
            await api.deleteProject(p.id);
            removeFromStore(p.id);
            // If we just deleted the active project, clear it.
            if (currentProject?.id === p.id) {
                setCurrentProject(null);
            }
            setActionsOpenFor(null);
        } catch (err: unknown) {
            logger.error('Failed to delete project', { error: errorMessage(err) });
            setError('Failed to delete project.');
        } finally {
            setBusyId(null);
        }
    };

    const goManagePlans = (p: Project): void => {
        setCurrentProject(p);
        // Open the floor-plan drawer mounted at App level. Closing
        // this project drawer first keeps the visual hand-off clean
        // (no overlapping bottom sheets — the floor-plan drawer
        // replaces the project drawer until the user dismisses it).
        onClose();
        openFloorPlanDrawer(p.id);
    };

    // Footer for the list view: a single "+ New project" CTA that
    // morphs the drawer body into the create form. The create view
    // ships its own Cancel / Create row inline, so the drawer's
    // footer slot stays empty in that mode.
    const listFooter = (
        <button
            type="button"
            onClick={startCreate}
            className="w-full flex items-center justify-center gap-2 bg-black text-yellow-300 font-bold rounded-xl border-2 border-black hover:bg-black/90 active:scale-[0.98] transition-all"
            style={{
                minHeight: 'var(--touch-target)',
                fontSize: 'clamp(14px, 3.8vw, 16px)',
            }}
        >
            <Plus size={18} aria-hidden="true" />
            <span>New project</span>
        </button>
    );

    return (
        <BottomDrawer
            open={open}
            onClose={view === 'create' ? cancelCreate : onClose}
            title={view === 'create' ? 'New project' : 'Select project'}
            footer={view === 'list' ? listFooter : undefined}
        >
            {view === 'create' ? (
                // Create-project form — same yellow-on-black-ink palette
                // as the list view. Cancel returns to list; on success
                // we switch back to list and the new project shows up
                // automatically (addProject() updates the store).
                <div className="space-y-3 pb-2">
                    <label className="block">
                        <span
                            className="block text-black font-semibold mb-1"
                            style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                        >
                            Project name
                        </span>
                        <input
                            type="text"
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            placeholder="e.g. Site Tower B"
                            className="w-full px-3 bg-yellow-100/80 border border-black/30 text-black placeholder-black/40 focus:bg-white focus:border-black focus:outline-none rounded-xl"
                            style={{
                                minHeight: 'var(--touch-target)',
                                fontSize: 'clamp(14px, 3.8vw, 16px)',
                            }}
                            disabled={isCreating}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && createName.trim()) {
                                    e.preventDefault();
                                    submitCreate();
                                }
                            }}
                            autoFocus
                        />
                    </label>

                    <label className="block">
                        <span
                            className="block text-black font-semibold mb-1"
                            style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                        >
                            Description{' '}
                            <span className="font-normal text-black/55">(optional)</span>
                        </span>
                        <textarea
                            value={createDescription}
                            onChange={(e) => setCreateDescription(e.target.value)}
                            placeholder="Notes about the site, the work, the client…"
                            rows={3}
                            className="w-full px-3 py-2 bg-yellow-100/80 border border-black/30 text-black placeholder-black/40 focus:bg-white focus:border-black focus:outline-none rounded-xl resize-none"
                            style={{ fontSize: 'clamp(13px, 3.4vw, 15px)' }}
                            disabled={isCreating}
                        />
                    </label>

                    {error && (
                        <div
                            role="alert"
                            className="rounded-xl border border-red-700/50 bg-red-500/15 px-3 py-2 text-red-800 leading-snug"
                            style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                        >
                            {error}
                        </div>
                    )}

                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            onClick={cancelCreate}
                            disabled={isCreating}
                            className="px-4 rounded-xl border border-black/30 bg-black/5 text-black hover:bg-black/10 active:bg-black/15 font-bold transition-colors disabled:opacity-50"
                            style={{
                                minHeight: 'var(--touch-target)',
                                fontSize: 'clamp(13px, 3.4vw, 15px)',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submitCreate}
                            disabled={!createName.trim() || isCreating}
                            className="flex-1 flex items-center justify-center gap-2 rounded-xl border-2 border-black bg-black text-yellow-300 hover:bg-black/90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed font-bold transition-all"
                            style={{
                                minHeight: 'var(--touch-target)',
                                fontSize: 'clamp(13px, 3.6vw, 15px)',
                            }}
                        >
                            {isCreating ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" />
                                    Creating…
                                </>
                            ) : (
                                <>
                                    <Plus size={16} />
                                    Create project
                                </>
                            )}
                        </button>
                    </div>
                </div>
            ) : (
                <>
            {/* Search */}
            <div className="relative mb-3">
                <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-black/60 pointer-events-none"
                    size={18}
                    aria-hidden="true"
                />
                <input
                    type="search"
                    placeholder="Search projects…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full bg-yellow-100/80 border border-black/30 text-black placeholder-black/40 focus:bg-white focus:outline-none rounded-xl pl-10 pr-3 font-mono"
                    style={{
                        fontSize: 'clamp(13px, 3.6vw, 16px)',
                        minHeight: 'var(--touch-target)',
                    }}
                />
            </div>

            {error && (
                <div
                    role="alert"
                    className="mb-2 rounded-xl border border-red-500/60 bg-red-500/15 px-3 py-2 text-red-700"
                    style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}
                >
                    {error}
                </div>
            )}

            {/* List */}
            <div className="divide-y divide-black/10">
                {/* Deselect — only when a project is currently active. */}
                {currentProject && (
                    <button
                        type="button"
                        onClick={deselect}
                        className="w-full flex items-center gap-3 py-3 hover:bg-black/5 active:bg-black/10 text-left transition-colors"
                        style={{ minHeight: 'var(--touch-target)' }}
                    >
                        <div className="w-9 h-9 rounded-xl bg-black/10 border border-black/20 flex items-center justify-center flex-shrink-0">
                            <X className="text-black/70" size={16} aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div
                                className="text-black font-semibold leading-tight"
                                style={{ fontSize: 'clamp(14px, 3.8vw, 16px)' }}
                            >
                                No project
                            </div>
                            <div
                                className="text-black/60 leading-tight mt-0.5"
                                style={{ fontSize: 'clamp(11px, 3vw, 13px)' }}
                            >
                                Regular photos without watermark
                            </div>
                        </div>
                    </button>
                )}

                {filtered.length === 0 ? (
                    <div
                        className="py-6 text-center text-black/60"
                        style={{ fontSize: 'clamp(13px, 3.6vw, 15px)' }}
                    >
                        {query ? 'No matches.' : 'No projects yet.'}
                    </div>
                ) : (
                    filtered.map((p) => {
                        const active = currentProject?.id === p.id;
                        const isRenaming = renamingId === p.id;
                        const isActionsOpen = actionsOpenFor === p.id;
                        const isBusy = busyId === p.id;

                        return (
                            <div key={p.id} className="py-1">
                                {/* Row body: tap to switch, kebab to open actions.
                                    Not a single <button> because the kebab is its
                                    own button (nested-button is invalid HTML). */}
                                <div className="w-full flex items-center gap-2">
                                    {isRenaming ? (
                                        <>
                                            <div className="w-9 h-9 rounded-xl bg-black text-yellow-300 border border-black flex items-center justify-center flex-shrink-0">
                                                <Pencil size={16} aria-hidden="true" />
                                            </div>
                                            <input
                                                type="text"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveRename(p);
                                                    if (e.key === 'Escape') cancelRename();
                                                }}
                                                disabled={isBusy}
                                                autoFocus
                                                className="flex-1 min-w-0 bg-yellow-100/90 border border-black/40 text-black focus:bg-white focus:outline-none rounded-xl px-3 py-2 font-bold"
                                                style={{
                                                    fontSize: 'clamp(13px, 3.6vw, 15px)',
                                                    minHeight: 'var(--touch-target)',
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => saveRename(p)}
                                                disabled={isBusy || !renameValue.trim()}
                                                className="flex items-center justify-center rounded-xl bg-black text-yellow-300 hover:bg-black/90 active:scale-95 disabled:opacity-40 transition-all flex-shrink-0"
                                                style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
                                                aria-label="Save name"
                                            >
                                                <Check size={18} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={cancelRename}
                                                disabled={isBusy}
                                                className="flex items-center justify-center rounded-xl bg-black/10 text-black hover:bg-black/20 active:scale-95 disabled:opacity-40 transition-all flex-shrink-0"
                                                style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
                                                aria-label="Cancel"
                                            >
                                                <X size={18} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => select(p)}
                                                disabled={isBusy}
                                                className={cx(
                                                    'flex-1 min-w-0 flex items-center gap-3 py-2 px-1 -mx-1 rounded-xl text-left transition-colors',
                                                    active ? 'bg-black/10' : 'hover:bg-black/5 active:bg-black/10',
                                                    isBusy && 'opacity-50',
                                                )}
                                                style={{ minHeight: 'var(--touch-target)' }}
                                                aria-current={active ? 'true' : undefined}
                                            >
                                                <div
                                                    className={cx(
                                                        'w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border',
                                                        active
                                                            ? 'bg-black text-yellow-300 border-black'
                                                            : 'bg-black/10 text-black/70 border-black/20',
                                                    )}
                                                >
                                                    <Folder size={16} aria-hidden="true" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            className="text-black font-bold truncate leading-tight"
                                                            style={{ fontSize: 'clamp(14px, 3.8vw, 16px)' }}
                                                        >
                                                            {p.name}
                                                        </span>
                                                        {active && (
                                                            <span
                                                                className="font-mono font-bold text-yellow-300 bg-black px-1.5 py-0.5 rounded tracking-[0.18em] flex-shrink-0"
                                                                style={{ fontSize: '9px', lineHeight: 1 }}
                                                            >
                                                                ACTIVE
                                                            </span>
                                                        )}
                                                    </div>
                                                    {p.description && (
                                                        <div
                                                            className="text-black/60 truncate leading-tight mt-0.5"
                                                            style={{ fontSize: 'clamp(11px, 3vw, 13px)' }}
                                                        >
                                                            {p.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => toggleActions(p.id)}
                                                disabled={isBusy}
                                                aria-label="Project actions"
                                                aria-expanded={isActionsOpen}
                                                className={cx(
                                                    'flex items-center justify-center rounded-xl transition-colors flex-shrink-0',
                                                    isActionsOpen
                                                        ? 'bg-black/15 text-black'
                                                        : 'text-black/60 hover:bg-black/10 hover:text-black active:bg-black/15',
                                                )}
                                                style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Inline action strip — appears only when the
                                    kebab on this row is toggled open. */}
                                {isActionsOpen && !isRenaming && (
                                    <div
                                        className="mt-1 flex gap-2 pl-12 pr-1"
                                        role="menu"
                                    >
                                        <ActionPill
                                            icon={<Pencil size={14} />}
                                            label="Rename"
                                            onClick={() => startRename(p)}
                                            disabled={isBusy}
                                        />
                                        <ActionPill
                                            icon={<Layers size={14} />}
                                            label="Manage plans"
                                            onClick={() => goManagePlans(p)}
                                            disabled={isBusy}
                                        />
                                        <ActionPill
                                            icon={<Trash2 size={14} />}
                                            label="Delete"
                                            onClick={() => confirmDelete(p)}
                                            disabled={isBusy}
                                            tone="danger"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
                </>
            )}
        </BottomDrawer>
    );
};

interface ActionPillProps {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    tone?: 'neutral' | 'danger';
}

const ActionPill: React.FC<ActionPillProps> = ({ icon, label, onClick, disabled, tone = 'neutral' }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        role="menuitem"
        className={cx(
            'flex-1 flex items-center justify-center gap-1.5 rounded-xl border font-bold transition-all',
            'active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed',
            tone === 'danger'
                ? 'bg-red-500/10 border-red-500/40 text-red-700 hover:bg-red-500/20 active:bg-red-500/30'
                : 'bg-black/5 border-black/25 text-black hover:bg-black/10 active:bg-black/15',
        )}
        style={{
            minHeight: 'min(40px, 11vw)',
            fontSize: 'clamp(11px, 3vw, 13px)',
        }}
    >
        {icon}
        <span>{label}</span>
    </button>
);

export default ProjectBottomSheet;
